<template>
  <!-- CONTENIDO -->
  <div class="blog-grid-page">
    <!-- Hero -->
    <section class="hero-section">
      <img
        class="hero-img"
        :src="bannerHero"
        alt="Industria 4.0"
      />
    </section>

    <!-- Filtros -->
    <div class="container filter-bar" data-aos="fade-up" data-aos-duration="6000">
      <div class="category-filters">
        <button
          v-for="cat in ['all', ...categories]"
          :key="cat"
          :class="['category-btn', { active: selectedCategory === cat }]"
          @click="selectCategory(cat)"
        >
          {{ cat === 'all' ? $t('all', selectedLang) : cat }}
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="isLoading" class="loader">
      Cargando artículos...
    </div>

    <!-- Estado vacío -->
    <div v-else-if="pagedArticles.length === 0" class="empty-state">
      No hay artículos en esta categoría.
    </div>

    <!-- Grid de artículos -->
    <div v-else class="container articles-grid">
      <div class="row">
        <div
          v-for="article in pagedArticles"
          :key="article.id + article.slug"
          class="col-12 col-md-6 col-lg-4 d-flex"
          data-aos="zoom-in-up"
          data-aos-duration="1200"
          data-aos-offset="200"
          data-aos-easing="ease-out-cubic"
        >
          <div class="blog-card">
            <!-- Imagen enlazada al post -->
            <router-link :to="{ name: 'BlogPost', params: { slug: article.slug } }">
              <img
                class="blog-img"
                :src="getArticleImage(article)"
                :alt="article.title[selectedLang]"
              />
            </router-link>

            <div class="blog-card-body">
              <div class="blog-meta">
                <span class="blog-author">
                  <i class="fa fa-user"></i> {{ article.author.name }}
                </span>
                <span class="blog-date">
                  <i class="fa fa-calendar"></i> {{ formatDate(article.date) }}
                </span>
              </div>

              <router-link
                :to="{ name: 'BlogPost', params: { slug: article.slug } }"
                class="blog-title-link"
                style="text-decoration:none"
              >
                <h3 class="blog-title">{{ article.title[selectedLang] }}</h3>
              </router-link>

              <div class="blog-summary">
                {{ getArticleSummary(article) }}
              </div>

              <router-link
                :to="{ name: 'BlogPost', params: { slug: article.slug } }"
                class="leer-mas-link"
              >
                Leer más
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginación -->
      <nav class="pagination-nav">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: page === 1 }">
            <button class="page-link" @click="goToPage(page - 1)" :disabled="page === 1">&laquo;</button>
          </li>
          <li
            class="page-item"
            v-for="n in totalPages"
            :key="n"
            :class="{ active: n === page }"
          >
            <button class="page-link" @click="goToPage(n)">{{ n }}</button>
          </li>
          <li class="page-item" :class="{ disabled: page === totalPages }">
            <button class="page-link" @click="goToPage(page + 1)" :disabled="page === totalPages">&raquo;</button>
          </li>
        </ul>
      </nav>
    </div>
  </div>

  <!-- FOOTER FUERA del wrapper para no heredar estilos -->
  <SiteFooter :selectedLang="selectedLang" />
</template>

<script>
import { useSEO } from '@/composables/useSEO';
import { database, ref, get, query, orderByChild } from '@/firebase.js'
import SiteFooter from '@/components/SiteFooter.vue'
import AOS from 'aos'
import 'aos/dist/aos.css'

const TRANSLATIONS = {
  all: { es: 'Todos', en: 'All', de: 'Alle' }
}

export default {
  name: 'BlogGrid',
  components: { SiteFooter },
  props: { selectedLang: { type: String, default: 'es' } },
  setup() {
    useSEO({
      title: 'Blog de Ingeniería Industrial',
      description: 'Artículos, noticias y tendencias sobre Industria 4.0, automatización, SCADA, IoT y transformación digital escritos por los expertos de Frenkel Engineering.',
      path: '/blog',
    });
  },
  data () {
    return {
      isLoading: true,
      selectedCategory: 'all',
      page: 1,
      perPage: 6,
      bannerHero: new URL('@/assets/images/hero_blog.png', import.meta.url).href,
      blogArticles: [] // Aquí guardaremos los artículos de Firebase
    }
  },
  computed: {
    articles () {
      return [...this.blogArticles]
        .filter(a => a.language.includes(this.selectedLang))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
    categories () {
      const allCats = this.articles.flatMap(a => a.category[this.selectedLang] || [])
      return [...new Set(allCats)]
    },
    filteredArticles () {
      if (this.selectedCategory === 'all') return this.articles
      return this.articles.filter(a =>
        (a.category[this.selectedLang] || []).includes(this.selectedCategory)
      )
    },
    pagedArticles () {
      const start = (this.page - 1) * this.perPage
      return this.filteredArticles.slice(start, start + this.perPage)
    },
    totalPages () {
      return Math.ceil(this.filteredArticles.length / this.perPage) || 1
    }
  },
  methods: {
    async fetchArticles () {
      try {
        this.isLoading = true
        
        // Referencia al nodo 'blogArticle' en Realtime Database
        const articlesRef = ref(database, 'blogArticles')
        
        // Query para ordenar por fecha de creación (opcional)
        const articlesQuery = query(articlesRef, orderByChild('createdAt'))
        
        // Obtener los datos
        const snapshot = await get(articlesQuery)
        
        if (snapshot.exists()) {
          const data = snapshot.val()
          
          // Convertir el objeto a array y agregar el ID
          this.blogArticles = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          }))
          
          console.log('Artículos cargados:', this.blogArticles.length)
        } else {
          console.log('No se encontraron artículos')
          this.blogArticles = []
        }
      } catch (error) {
        console.error('Error al cargar artículos:', error)
        this.blogArticles = []
      } finally {
        this.isLoading = false
      }
    },
    selectCategory (cat) {
      this.selectedCategory = cat
      this.page = 1
    },
    goToPage (n) {
      if (n >= 1 && n <= this.totalPages) this.page = n
    },
    formatDate (dateStr) {
      return new Date(dateStr).toLocaleDateString(this.selectedLang, {
        year: 'numeric', month: 'short', day: '2-digit'
      })
    },
    getArticleImage (article) {
      //const firstImg = (article.content[this.selectedLang] || []).find(c => c.type === 'image')
      /*eturn firstImg
        ? firstImg.url
        : article.headerImage || 'https://www.edsrobotics.com/wp-content/uploads/2020/05/industria-4.0-900x480.jpg';*/
        return article.headerImage
    },
    getArticleSummary (article) {
      const para = (article.content[this.selectedLang] || []).find(c => c.type === 'paragraph')
      if (!para) return ''
      const text = para.text?.[this.selectedLang] ?? para.text ?? ''
      return text.length > 240 ? text.slice(0, 237).trim() + '...' : text
    },
    $t (key, lang) {
      return TRANSLATIONS[key]?.[lang] || key
    }
  },
  async mounted () {
    // Cargar artículos de Firebase
    await this.fetchArticles()
    
    // Inicializar AOS
    AOS.init({ duration: 800, once: true })
    setTimeout(() => AOS.refresh(), 300)
  }
}
</script>

<style scoped>
.blog-grid-page {
  background: #f6fafb;
  /* Importante: que el wrapper no afecte al footer */
  padding-bottom: 32px;
  overflow-x: hidden;
}

.hero-section { width: 100%; background: #e0ebf7; }
.hero-img {
  width: 100%;
  height: 320px;
  object-fit: cover;
  object-position: center;
  border-bottom: 1.5px solid #dde;
}

.filter-bar {
  margin-top: -24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
}
.category-filters {
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  background: #fff;
  border-radius: 17px;
  box-shadow: 0 4px 12px 0 rgba(10, 20, 30, 0.10);
}
.category-btn {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.97rem;
  padding: 5px 18px;
  border: 1.5px solid #c7c7c7;
  border-radius: 999px;
  background: none;
  cursor: pointer;
  transition: background-color .3s, transform .3s, box-shadow .3s, color .2s, border-color .2s;
}
.category-btn:hover {
  background: #2c74b8;
  color: #fff;
  border-color: #2c74b8;
  transform: scale(1.03);
  box-shadow: 0 4px 12px rgba(0, 0, 0, .2);
}
.category-btn.active {
  background: #e6f0f8;
  border-color: #1182c6;
  color: #1182c6;
}

.articles-grid { margin-bottom: 48px; }
.blog-card {
  background: #fff;
  border: 1px solid transparent;
  border-radius: 13px;
  box-shadow: 0 2px 12px rgba(10, 20, 30, .08);
  overflow: hidden;
  margin-bottom: 22px;
  display: flex;
  flex-direction: column;
  width: 100%;
  transition: transform .3s, box-shadow .3s, border-color .2s;
}
.blog-card:hover {
  transform: scale(1.03);
  box-shadow: 0 10px 38px rgba(16, 46, 90, .14);
  border-color: #2c74b8;
}
.blog-img {
  width: 100%;
  height: 180px;
  object-fit: cover;
  object-position: center;
  background: #e6e6e6;
}
.blog-card-body { padding: 17px 18px 12px; flex: 1; display: flex; flex-direction: column; }

.blog-meta { font-size: .93rem; color: #444; margin-bottom: 8px; display: flex; align-items: center; gap: 10px; }

.blog-title {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 1.04rem;
  font-weight: 700;
  margin-bottom: 7px;
  min-height: 50px;
  color: #101;
  transition: color .18s;
}
.blog-title-link:hover .blog-title { color: #ef7b1a; }

.blog-summary { color: #232323; font-size: .98rem; margin-bottom: 6px; min-height: 60px; }

.leer-mas-link {
  color: #1182c6;
  font-family: 'IBM Plex Mono', monospace;
  font-size: .99rem;
  text-decoration: underline;
  margin-top: 4px;
  display: inline-block;
  transition: color .16s;
}
.leer-mas-link:hover { color: #ef7b1a; }

.pagination-nav { margin: 10px 0 38px; }
.pagination .page-item .page-link {
  font-family: 'IBM Plex Mono', monospace;
  border-radius: 7px;
  color: #1170b0;
  border: 1px solid #b2d1e7;
  background: #fff;
  margin: 0 2px;
  padding: 5px 12px;
  transition: all .18s;
}
.pagination .page-item.active .page-link,
.pagination .page-item .page-link:hover {
  background: #1182c6;
  color: #fff;
  border-color: #1182c6;
}

.loader, .empty-state {
  text-align: justify;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 1.2rem;
  margin: 42px 0 38px;
  color: #888;
}

@media (max-width: 767.98px) {
  .hero-img { height: 160px; }
  .category-filters { flex-wrap: wrap; }
  .articles-grid .col-md-6, .articles-grid .col-lg-4 {
    max-width: 100%;
    flex: 0 0 100%;
  }
}
</style>